import { inViewport, isOverlapping } from 'utils/dom';

import { EDITOR_ACTIVE_PAGE_FOCUS_ID, EDITOR_TOOLBAR_ID, WIDGET_MENU_ID } from '../Editor.config';
import { SCROLL_CONTAINER_ID } from '../components/PageArea/PageArea.config';
import { SIDE_BAR_ID } from '../components/WidgetMenu/Sidebar/Sidebar.config';
import { selectZoom } from './editorSettingsSelector';
import { selectInfographHeightPx, selectInfographWidthPx } from './infographSelector';
import { store } from './store';

const INITIAL_LEFTPX_RATIO = 0.15;
const INITIAL_LEFTPX_RATIO_CHARTS = 0.1;

/**
 * Get the initial position of a new widget so that its within the zoomed/scrolled view
 * of the page container / scroll container
 *
 * @param zoom - the zoom level
 * @param leftRatio - the left margin
 */
export const calculateInitialTopPx = (zoom: number, leftRatio: number): { topPx: number; leftPx: number } => {
  const pageContainerEl = document.getElementById(SCROLL_CONTAINER_ID);
  const sideBarEl = document.getElementById(SIDE_BAR_ID);

  if (!pageContainerEl || !sideBarEl) return { topPx: 0, leftPx: 0 };

  const pageScrollTopOffset = pageContainerEl.scrollTop || 0;
  const pageScrollLeftOffset = pageContainerEl.scrollLeft || 0;
  const widgetMenuHeight = sideBarEl?.offsetHeight || 0;
  const pageContainerWidth = pageContainerEl.offsetWidth;

  // Assuming a similar logic for left position as used for top position,
  // you might adjust the left position by a certain percentage of the page container's width
  // Here, using 0.15 of the container's width for non-chart widgets and adjusting for zoom
  const leftPx = (pageScrollLeftOffset + leftRatio * pageContainerWidth) / zoom;

  // Adjust the top position similarly, adding 30% of widget menu height to the scroll offset and adjusting for zoom
  const topPx = (pageScrollTopOffset + 0.3 * widgetMenuHeight) / zoom;

  return { topPx, leftPx };
};

/**
 * Get the initial position of a new widget so that its within the zoomed/scrolled view
 * The logic is to first get the scrollTop attribute of pageContainer element, the scrolling Px to the top, and add 30% of widget menu height
 *
 * @param isChart
 * @returns
 */
export const getInitialWidgetPosition = (isChart = false) => {
  const storeState = store.getState();

  const zoom = selectZoom(storeState);
  const infographWidthPx = selectInfographWidthPx(storeState);
  const infographHeightPx = selectInfographHeightPx(storeState);

  // the left margin - a bit more for charts since the side menu is open
  const leftRatio = isChart ? INITIAL_LEFTPX_RATIO_CHARTS : INITIAL_LEFTPX_RATIO;
  const { topPx, leftPx } = calculateInitialTopPx(zoom, leftRatio);

  return {
    top: topPx,
    left: leftPx,
    infographHeight: infographHeightPx,
    infographWidth: infographWidthPx,
  };
};

/**
 * Check if a widget is in view in the Editor by using the widget's dimensions
 * @param widgetDimensions - the widget's dimensions
 *
 * @returns - true if the widget is in view, false otherwise
 */
export function isWidgetInView(widgetDimensions: { topPx: number; leftPx: number; widthPx: number; heightPx: number }) {
  const editorPageEl = document.getElementById(EDITOR_ACTIVE_PAGE_FOCUS_ID);
  if (!editorPageEl) return false;

  const { topPx, leftPx, widthPx, heightPx } = widgetDimensions;

  // create a invisible div and place it in the editor
  const div = document.createElement('div');
  div.style.position = 'absolute';
  div.style.top = `${topPx}px`;
  div.style.left = `${leftPx}px`;
  div.style.width = `${widthPx}px`;
  div.style.height = `${heightPx}px`;
  div.style.opacity = '0';
  div.style.pointerEvents = 'none';
  div.style.zIndex = '0';

  // add it to the active page
  editorPageEl.appendChild(div);

  // if the widget is under the side menu, remove the div and return false
  const isBelowSideMenu = isOverlapping(div, document.getElementById(WIDGET_MENU_ID));
  if (isBelowSideMenu) {
    editorPageEl.removeChild(div);
    return false;
  }

  // if the widget is under the top bar, remove the div and return false
  const isBelowTopBar = isOverlapping(div, document.getElementById(EDITOR_TOOLBAR_ID), true);
  if (isBelowTopBar) {
    editorPageEl.removeChild(div);
    return false;
  }

  let isInView = inViewport(div);
  editorPageEl.removeChild(div);

  return isInView;
}
